import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Loader2, AlertCircle, Home } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function GuestGoogleCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const handleCallback = async () => {
      // Emergent Auth returns session_id in the URL hash fragment
      const hashParams = new URLSearchParams(location.hash.replace('#', ''));
      const sessionId = hashParams.get('session_id');
      let returnUrl = searchParams.get('return') || '/marketplace';
      
      // SECURITY: Ensure we only redirect to marketplace routes, not host routes
      if (!returnUrl.startsWith('/marketplace')) {
        returnUrl = '/marketplace';
      }
      
      // Also check for old-style code (for backwards compatibility)
      const code = searchParams.get('code');
      
      if (!sessionId && !code) {
        setError('No authorization received. Please try again.');
        return;
      }
      
      try {
        let endpoint, body;
        
        if (sessionId) {
          // Use Emergent Auth session endpoint
          endpoint = '/api/marketplace/auth/google/session';
          body = { session_id: sessionId };
        } else {
          // Fallback to code-based auth
          endpoint = '/api/marketplace/auth/google/callback';
          body = { code };
        }
        
        const resp = await fetch(`${BACKEND_URL}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        
        const data = await resp.json();
        
        if (!resp.ok) {
          throw new Error(data.detail || 'Google authentication failed');
        }
        
        // Store GUEST token and user info (separate from host auth)
        // Clear any host tokens to avoid confusion
        localStorage.setItem('guest_token', data.token);
        localStorage.setItem('guest_user', JSON.stringify(data.guest));
        
        // Redirect to marketplace return URL (never to host dashboard)
        navigate(returnUrl);
        
      } catch (err) {
        setError(err.message);
      }
    };
    
    handleCallback();
  }, [searchParams, location.hash, navigate]);
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Authentication Failed</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/marketplace/login')}
            className="px-6 py-3 bg-[#FF385C] text-white rounded-xl font-semibold hover:bg-[#E31C5F]"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-[#FF385C] to-[#E31C5F] rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Home className="w-8 h-8 text-white" />
        </div>
        <Loader2 className="w-8 h-8 animate-spin text-[#FF385C] mx-auto mb-4" />
        <p className="text-gray-600">Signing you in...</p>
      </div>
    </div>
  );
}
